import axios, { AxiosResponse } from "axios";
import { isCustomDomain } from "../utils/navigation";

export const PATH = "https://s1.proste.menu"; // Base URL for the API

// Module-level slug — set from React context via setApiSlug()
let _slug: string | null = null;

export const setApiSlug = (slug: string | null) => {
  _slug = slug;
};

// Shared axios instance with slug interceptor
const api = axios.create({ baseURL: PATH });

api.interceptors.request.use((config) => {
  const customDomain = isCustomDomain();

  // Na zwyklej domenie (nie custom) firme identyfikuje slug. Zanim React ustawi
  // go przez setApiSlug(), NIE wysylamy zapytania - bez slug/companyId/custom
  // domeny backend nie potrafi ustalic firmy i zwraca 400. Anulujemy zadanie;
  // React Query ponowi je, gdy slug bedzie juz ustawiony.
  if (!customDomain && !_slug) {
    throw new axios.Cancel(
      "Slug firmy jeszcze niezaladowany - pomijam zapytanie do czasu zaladowania"
    );
  }

  if (_slug && !customDomain) {
    config.params = { ...config.params, slug: _slug };
  }
  return config;
});

export const getServices = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/services_api/services/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getServicesCategory = async (id: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/services_api/service_categories`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getBranches = async ({ id }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/common_api/company_branches`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAvailabilityOnDate = async ({
  id,
  dateToBeChecked,
}: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/services_api/service_availability/${id}/${dateToBeChecked}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAvailabilityForDateRange = async ({
  id,
  daysCount,
}: {
  id: string | undefined;
  daysCount: number;
}): Promise<Record<string, string[]>> => {
  try {
    const response: AxiosResponse<Record<string, string[]>> = await api.get(
      `/v1/services_api/service_availability_range/${id}/${daysCount}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDelivery = async ({ id }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/api/index.php?file=delivery`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createServiceOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.post<any>(
      `/v1/services_api/service_order`,
      {
        ...data,
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const createProductOrder = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.post<any>(
      `/v1/products_api/product_order`,
      {
        ...data,
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const createPayments = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.post<any>(
      `/v1/common_api/payments`,
      {
        ...data,
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getCompanyPayments = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/common_api/payment_methods`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProductCategories = async ({ id }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/products_api/client/categories/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProducts = async (id: number): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/products_api/products/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProductDetails = async ({ id, variant }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/products_api/product/${id}/${variant}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getGoogleMapsSuggestions = async ({
  search,
}: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/common_api/adress-seggestions/${search}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDeliveryPrice = async ({ place_id }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/common_api/delivery-price/${place_id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
export const getPlaceDetails = async ({ place_id }: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/common_api/place-details/${place_id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface ValidateAddressRequest {
  lat: number;
  lng: number;
  companyBranchId: number | string;
  orderValue?: number;
}

export interface ValidateAddressResponse {
  id: number;
  name: string;
  price: number;
  freeAbove: number | null;
  minimumOrderValue: number;
  actualDeliveryPrice: number;
  meetsMinimumOrder: boolean;
  missingForMinimum: number | null;
  missingForFreeDelivery: number | null;
}

export const validateDeliveryAddress = async (
  data: ValidateAddressRequest
): Promise<ValidateAddressResponse> => {
  const response: AxiosResponse<ValidateAddressResponse> = await api.post(
    `/v1/common_api/delivery-zone/calculate-price`,
    {
      lat: data.lat,
      lng: data.lng,
      companyBranchId: Number(data.companyBranchId),
      orderValue: data.orderValue ?? 0,
    }
  );
  return response.data;
};

export const getDeliveryZones = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/common_api/delivery-zones/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDeliveryMethods = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/common_api/delivery-methods/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getResourceCategories = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/services_api/resource-categories`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCategoryResources = async ({
  id,
}: {
  id: number | string;
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/services_api/resources/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCompanyData = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/common_api/client/company`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const validateCoupon = async (data: {
  code: string;
  companyBranchId: number | string;
  cartTotal: number;
  productIds: number[];
}): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.post<any>(
      `/v1/common_api/coupons/validate`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get company slug by custom domain (for white-label client domains)
// Uses raw axios (not api instance) since this call happens before slug is known
export const getCompanySlugByCustomDomain = async (customDomain: string): Promise<{ slug: string } | null> => {
  try {
    const response: AxiosResponse<{ slug: string }> = await axios.get(
      `${PATH}/v1/common_api/company/by-custom-domain?customDomain=${encodeURIComponent(customDomain)}`
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

// LOYALTY — public client endpoint
export const getClientLoyaltyCards = async (phone: string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.get<any>(
      `/v1/common_api/loyalty/my-cards`,
      { params: { phone } }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
